import { formatMoney } from '@cartiq/shared';
import type { BookingCard as BookingCardData } from '../types';

interface Props {
  booking: BookingCardData;
  checkoutUrl?: string;
}

const STATUS_LABEL: Record<BookingCardData['status'], string> = {
  requested: 'Requested',
  quoted: 'Quoted',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
};

function slotLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function BookingCard({ booking, checkoutUrl }: Props) {
  const amount = booking.depositAmount ?? booking.quotedAmount ?? null;

  return (
    <div
      className="w-full rounded-md border border-border bg-white p-4 shadow-card animate-pop-in"
      style={{ borderLeft: '4px solid var(--brand-accent)' }}
    >
      <div className="flex items-center gap-2 text-mid">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="18" rx="2" />
          <path d="M16 2v4M8 2v4M3 10h18M9 16l2 2 4-4" />
        </svg>
        <span className="text-xs font-semibold uppercase tracking-wide">Booking</span>
      </div>

      <p className="mt-2 text-base font-bold text-primary">{booking.offeringName}</p>

      {booking.scheduledStart && (
        <p className="mt-1 text-sm text-primary">{slotLabel(booking.scheduledStart)}</p>
      )}

      {booking.staffName && (
        <p className="mt-1 text-sm text-mid">with {booking.staffName}</p>
      )}

      <p className="mt-1 text-xs text-mid">
        Status: <span className="font-medium text-primary">{STATUS_LABEL[booking.status]}</span>
        {amount != null && ` · ${formatMoney(amount, booking.currency)}`}
      </p>

      {booking.depositAmount != null && booking.balanceAmount != null && booking.balanceAmount > 0 && (
        <p className="mt-1 text-xs text-mid">
          Deposit{' '}
          <span className="font-medium text-primary">
            {formatMoney(booking.depositAmount, booking.currency)}
          </span>
          {booking.depositPaid && ' (paid)'}
          {' · Balance '}
          <span className="font-medium text-primary">
            {formatMoney(booking.balanceAmount, booking.currency)}
          </span>
          {booking.balancePaid ? ' (paid)' : ' due after service'}
        </p>
      )}

      {checkoutUrl ? (
        <a
          href={checkoutUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-3 flex w-full items-center justify-center gap-1.5 rounded-sm px-4 py-2.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(108,99,255,0.3)] transition-all hover:brightness-105 active:scale-[0.98]"
          style={{ backgroundColor: 'var(--brand-accent)' }}
        >
          {booking.depositAmount != null ? 'Pay deposit' : 'Complete booking'}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="transition-transform duration-200 group-hover:translate-x-0.5">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </a>
      ) : (
        <p className="mt-2 text-xs text-mid">
          We&apos;ll follow up shortly with a price for your request.
        </p>
      )}
    </div>
  );
}

export default BookingCard;
